import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Link2, ClipboardCopy, Loader2, Mail, Check } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRICES = { 1: 44.99, 2: 84.99, 3: 113.99 };

export const PaymentLinkGenerator = () => {
  const [quantity, setQuantity] = useState(1);
  const [firstTime, setFirstTime] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [sendEmail, setSendEmail] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  const displayPrice = quantity === 1 && firstTime ? 37.99 : PRICES[quantity];

  const generate = async () => {
    if (sendEmail && !email.trim()) {
      toast.error('Enter the customer email to send the link');
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const r = await axios.post(`${API}/admin/payment-links`, {
        quantity,
        first_time: firstTime,
        customer_name: name.trim() || null,
        customer_email: email.trim() || null,
        send_email: sendEmail,
        origin_url: window.location.origin,
      });
      setResult(r.data);
      toast.success(r.data.emailed ? 'Payment link created & emailed' : 'Payment link created');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to create payment link');
    } finally {
      setLoading(false);
    }
  };

  const copy = async () => {
    if (!result?.payment_link) return;
    await navigator.clipboard.writeText(result.payment_link);
    setCopied(true);
    toast.success('Link copied');
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="bg-white rounded-2xl border border-border p-6 max-w-2xl" data-testid="payment-link-generator">
      <div className="flex items-center gap-2 mb-1">
        <Link2 className="w-5 h-5 text-blue-600" />
        <h3 className="text-lg font-heading font-semibold">Generate a Payment Link</h3>
      </div>
      <p className="text-sm text-muted-foreground mb-6">
        Create a secure Stripe checkout link for a customer who couldn't pay online. Copy it or email it directly.
        The customer enters their <strong>delivery address</strong> on the Stripe page, and their <strong>VAT invoice is emailed automatically</strong> once payment completes — the order then appears in Orders ready to ship.
      </p>

      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium mb-1.5">Number of bottles</label>
          <select
            value={quantity}
            onChange={(e) => { const q = Number(e.target.value); setQuantity(q); if (q !== 1) setFirstTime(false); }}
            className="w-full border border-border rounded-xl px-3 py-2.5 bg-white"
            data-testid="pl-quantity"
          >
            <option value={1}>1 bottle — £44.99</option>
            <option value={2}>2 bottles — £84.99</option>
            <option value={3}>3 bottles — £113.99 (Best Value)</option>
          </select>
        </div>
        <div className="flex items-end">
          <label className={`flex items-center gap-2 text-sm ${quantity !== 1 ? 'opacity-40' : ''}`}>
            <input
              type="checkbox"
              checked={firstTime}
              disabled={quantity !== 1}
              onChange={(e) => setFirstTime(e.target.checked)}
              className="w-4 h-4"
              data-testid="pl-first-time"
            />
            Apply first-time welcome price (£37.99)
          </label>
        </div>
        <div>
          <label className="block text-sm font-medium mb-1.5">Customer name (optional)</label>
          <input value={name} onChange={(e) => setName(e.target.value)} className="w-full border border-border rounded-xl px-3 py-2.5" placeholder="e.g. Diana Stone" data-testid="pl-name" />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1.5">Customer email {sendEmail && <span className="text-red-500">*</span>}</label>
          <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" className="w-full border border-border rounded-xl px-3 py-2.5" placeholder="customer@email.com" data-testid="pl-email" />
        </div>
      </div>

      <label className="flex items-center gap-2 text-sm mt-4">
        <input type="checkbox" checked={sendEmail} onChange={(e) => setSendEmail(e.target.checked)} className="w-4 h-4" data-testid="pl-send-email" />
        <Mail className="w-4 h-4 text-muted-foreground" /> Email the link to the customer automatically
      </label>

      <div className="flex items-center gap-3 mt-6">
        <button
          onClick={generate}
          disabled={loading}
          className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-5 py-2.5 rounded-xl font-medium disabled:opacity-50"
          data-testid="pl-generate"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />}
          {loading ? 'Creating…' : `Create link — £${displayPrice.toFixed(2)}`}
        </button>
      </div>

      {result && (
        <div className="mt-6 bg-emerald-50 border border-emerald-200 rounded-xl p-4" data-testid="pl-result">
          <p className="text-sm font-medium text-emerald-800 mb-2">
            Link ready · £{Number(result.amount).toFixed(2)} · {result.quantity} bottle(s)
            {result.first_time_applied ? ' · first-time price' : ''}{result.emailed ? ' · emailed ✓' : ''}
          </p>
          <div className="flex items-center gap-2">
            <input
              readOnly
              value={result.payment_link}
              className="flex-1 border border-emerald-300 rounded-lg px-3 py-2 text-sm bg-white"
              data-testid="pl-link"
              onFocus={(e) => e.target.select()}
            />
            <button onClick={copy} className="flex items-center gap-1 bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-2 rounded-lg text-sm font-medium" data-testid="pl-copy">
              {copied ? <Check className="w-4 h-4" /> : <ClipboardCopy className="w-4 h-4" />} {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
          <p className="text-xs text-emerald-700 mt-2">Invoice {result.invoice_number}. A pending order was created and will be marked paid automatically once the customer pays.</p>
        </div>
      )}
    </div>
  );
};
